
import { useEffect, useState } from "react";
import { BlogPost } from "@/types/blogTypes";
import { trackView as trackViewRequest } from "@/services/d1Service";
import { useToast } from "@/hooks/use-toast";

interface ViewTrackerProps {
  post: BlogPost;
}

/**
 * Component to track blog post views
 * This is a silent component that doesn't render anything
 * It just tracks views when the post is loaded
 */
const ViewTracker = ({ post }: ViewTrackerProps) => {
  // Use a state variable to ensure tracking only happens once
  const [hasTracked, setHasTracked] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Only track if we're in a browser environment, haven't tracked yet, and post exists
    const isBrowser = typeof window !== 'undefined';
    if (!isBrowser || hasTracked || !post?.id) return;

    const sendTrackView = async () => {
      try {
        console.log(`Attempting to track view for post: ${post.id}`);
        
        // Create a key that includes the post ID and the current day
        // This ensures we count at most one view per post per day per session
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
        const viewKey = `${post.id}-${today}`;
        
        // Check if this user has already viewed this post in this session
        const viewedPosts = JSON.parse(sessionStorage.getItem('viewedPosts') || '[]');
        
        if (viewedPosts.includes(viewKey)) {
          console.log(`Post ${post.id} already viewed today, not tracking again`);
          setHasTracked(true); // Mark as tracked to prevent future attempts
          return;
        }
        
        // Add this post to the viewed posts list using the day-specific key
        // We do this BEFORE the tracking to prevent duplicate requests
        viewedPosts.push(viewKey);
        sessionStorage.setItem('viewedPosts', JSON.stringify(viewedPosts));
        setHasTracked(true); // Mark as tracked immediately
        
        // Call the increment function using RPC with proper TypeScript handling
        await trackViewRequest(post.id);
        console.log(`View tracked successfully for post: ${post.id}`);
      } catch (error) {
        console.error('Error tracking view:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to track view. This won't affect your experience.",
        });
      }
    };

    // Add a small delay before checking sessionStorage
    // This helps prevent race conditions when multiple instances are initialized
    const timeoutId = setTimeout(() => {
      // Only track views if the post is published
      if (post.published) {
        sendTrackView();
      }
    }, 300); // 300ms delay to allow other instances to initialize
    
    return () => clearTimeout(timeoutId);
  }, [post.id, post.published, hasTracked, toast]);

  // This component doesn't render anything
  return null;
};

export default ViewTracker;
